import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Typography } from '@mui/material';

import ProgressTracker from '@ui-parts/ProgressTracker';
import InputWithLabel from '@ui-parts/InputWithLabel';
import HelpButton from '@ui-parts/HelpButton';
import Button from '@ui-elements/Button';
import HelpLink from '@ui-parts/HelpLink';

import useDidMountEffect from '@functional/useDidMountEffect';

import { Z_INDEX, FONT_WEIGHTS, PALETTE, FONT_SIZES } from '~/assets/styles/base';
import {
  SURVEY_TYPE,
  SURVEY_STATUSES,
  PROGRESS_TRACKER_LABELS,
  PROGRESS_TYPE,
  SURVEY_PROGRESSES,
  TEXT_MAX_LENGTH,
  SCREEN_VISIBILITY_SETTINGS,
  SCREEN_VISIBILITY_SETTINGS_IDS,
} from '~/constants';
import { ROUTES } from '~/constants/routes';
import { createSurvey, fetchUserScreenVisibilitySettings, callThunk } from '~/api';
import {
  surveyTitleValidationRule,
  surveyUrlValidationRule,
  numberOfQuestionsValidationRule,
} from '~/constants/validationRules';

import NgChecklistModal from '../NgChecklistModal';
import { SURVEY_FORM_TYPE } from '~/common/constants';

const HelpButtonBox = styled.div`
  position: fixed;
  top: 75%;
  right: 160px;
  z-index: ${Z_INDEX.max};
`;

const SurveyRegisterForm = styled.form`
  width: 721px;
  margin: auto;
`;
const ContentBox = styled.div`
  width: 100%;
  padding: 25px;
  margin: 20px 0;
  border: 1px solid ${PALETTE.border.frame};
  background: ${PALETTE.white};
`;

// エラーメッセージの矢印アイコン iconタグ読み込みだとタグがmessage側なのでlintエラーになる為、親divのスタイルで定義
const ContentBoxWithErrorArrowIcon = styled(ContentBox)`
  & [color='error'] a {
    position: relative;
    &:after {
      content: '';
      position: absolute;
      bottom: 6px;
      right: -10px;
      margin: auto;
      width: 5px;
      height: 5px;
      border-top: solid 1px ${PALETTE.error.main};
      border-right: solid 1px ${PALETTE.error.main};
      transform: rotate(45deg);
    }
  }
`;

const ButtonBox = styled.div`
  text-align: right;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  width: 100%;
  text-align: left;
  font-size: ${FONT_SIZES.title1};
`;

const Title = styled(Typography)`
  font-size: ${FONT_SIZES.title1};
`;

const Category = styled(Typography)`
  font-size: ${FONT_SIZES.title2};
  margin-right: 20px;
`;

const FlexAlignTop = styled.div`
  display: flex;
  align-items: top;
`;

const HelpLinkWrapper = styled.div`
  margin-left: 25px;
  padding-top: 52px;
`;

function SurveyRegisterGoogleForms() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const survey = useSelector((state) => state.survey);
  const user = useSelector((state) => state.user);

  const registerForm = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      surveyName: '',
      surveyUrl: '',
      numberOfQuestions: '',
    },
  });

  const [showNgChecklistModal, setShowNgChecklistModal] = useState(true);
  const [openNgChecklistModals, setOpenNgChecklistModals] = useState(false);

  const handleChangeSurveyTitle = (e) => {
    registerForm.setValue('surveyName', e.target.value);
  };

  const handleBlurSurveyTitle = (e) => {
    registerForm.setValue('surveyName', e.target.value.trim());
  };

  const handleChangeSurveyUrl = (e) => {
    registerForm.setValue('surveyUrl', e.target.value.trim());
  };

  const handleBlurSurveyUrl = (e) => {
    registerForm.setValue('surveyUrl', e.target.value.trim());
  };

  const handleChangeQuestionNumber = (e) => {
    registerForm.setValue('numberOfQuestion', e.target.value);
  };

  const handleOnKeyDownQuestionNumber = (e) => {
    const numberInputUnallowedKeys = ['e', 'E', '-'];

    if (numberInputUnallowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    setShowNgChecklistModal(
      !user.screenVisibilitySettings?.includes(
        SCREEN_VISIBILITY_SETTINGS_IDS[
          SCREEN_VISIBILITY_SETTINGS.SURVEY_REGISTER_NG_CHECKLIST_HIDDEN
        ]
      )
    );
  }, [user.screenVisibilitySettings]);

  useEffect(() => {
    const init = async () => {
      await dispatch({ type: 'survey/initialize' });
      await dispatch(
        callThunk({
          action: fetchUserScreenVisibilitySettings,
        })
      );
    };
    init();
  }, []);

  /**
   * navigate処理は、必ず1度マウントされた後、surveyがAPI経由で保存された場合のみに呼ばれる必要がある。
   * 1回目のマウント時には、initializeのみが行われる必要があるので、1回目のマウント時に発火しないようにuseDidMountEffectを使用。
   */
  useDidMountEffect(() => {
    if (survey.id !== null) {
      navigate(ROUTES.USER.SURVEY.replace(':id', survey.id), {
        state: { referrer: window.location.pathname },
      });
    }
  }, [survey.id]);

  const onSubmit = () => {
    dispatch(
      callThunk({
        action: createSurvey,
        payload: {
          name: registerForm.getValues('surveyName'),
          url: registerForm.getValues('surveyUrl'),
          numberOfQuestions: registerForm.getValues('numberOfQuestions'),
          type: SURVEY_TYPE.GOOGLE_FORM,
          surveyStatusId: SURVEY_STATUSES.REGISTERING,
        },
      })
    );
  };

  return (
    <>
      <ProgressTracker
        width="618px"
        labels={PROGRESS_TRACKER_LABELS[PROGRESS_TYPE.SURVEY].map((label) => t(label))}
        currentNum={SURVEY_PROGRESSES[ROUTES.USER.SURVEY_REGISTER_TYPE_SELECTION]}
      />

      <SurveyRegisterForm onSubmit={registerForm.handleSubmit(onSubmit)} noValidate>
        <TitleWrapper>
          <Category>{t('pageTitles.user.surveyRegister.category')}</Category>
          <Title>{t('pageTitles.user.surveyRegister.title')}</Title>
        </TitleWrapper>
        <ContentBox>
          <Controller
            name="surveyName"
            control={registerForm.control}
            rules={surveyTitleValidationRule}
            render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => {
              return (
                <InputWithLabel
                  label={t('survey.SurveyBasicInfoBox.surveyTitle')}
                  name={name}
                  type="text"
                  value={value}
                  placeholder={t('survey.SurveyBasicInfoBox.surveyTitleModal.placeholder')}
                  description={t('survey.SurveyBasicInfoBox.surveyTitleModal.desc')}
                  errorMessage={error?.message}
                  width="680px"
                  textAlign="left"
                  warnText={t('appCommon.forbiddenText')}
                  warnRight="10px"
                  warnBottom="50px"
                  warnLink={ROUTES.COMMON.FORBIDDEN_TEXT}
                  fontWeight={FONT_WEIGHTS.size1}
                  inputFontSize={FONT_SIZES.body3}
                  handleChange={(e) => {
                    onChange(e);
                    handleChangeSurveyTitle(e);
                  }}
                  handleBlur={(e) => {
                    onBlur(e);
                    handleBlurSurveyTitle(e);
                  }}
                  required
                  error={Boolean(error)}
                  errorZIndex={Z_INDEX.tooltip}
                  dataTestId="User-SurveyRegister-Title"
                  inputProps={{ maxLength: 70 }}
                  position="centerAbsolute"
                  ref={ref}
                />
              );
            }}
          />
        </ContentBox>
        <ContentBoxWithErrorArrowIcon>
          <Controller
            name="surveyUrl"
            control={registerForm.control}
            rules={surveyUrlValidationRule}
            render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => {
              return (
                <InputWithLabel
                  label={t('survey.SurveyBasicInfoBox.googleFormSurveyUrl')}
                  description={t('survey.SurveyBasicInfoBox.surveyUrlModal.desc')}
                  dangerousSetDescription
                  name={name}
                  type="text"
                  value={value}
                  placeholder={t('survey.SurveyBasicInfoBox.surveyUrlModal.placeholder')}
                  errorMessage={error?.message}
                  width="680px"
                  textAlign="left"
                  fontWeight={FONT_WEIGHTS.size1}
                  inputFontSize={FONT_SIZES.body3}
                  handleChange={(e) => {
                    onChange(e);
                    handleChangeSurveyUrl(e);
                  }}
                  handleBlur={(e) => {
                    onBlur(e);
                    handleBlurSurveyUrl(e);
                  }}
                  required
                  error={Boolean(error)}
                  errorZIndex={Z_INDEX.tooltip}
                  dataTestId="User-SurveyRegister-Url"
                  ref={ref}
                  inputProps={{ maxLength: TEXT_MAX_LENGTH }}
                />
              );
            }}
          />
        </ContentBoxWithErrorArrowIcon>
        <ContentBox>
          <FlexAlignTop>
            <Controller
              name="numberOfQuestions"
              control={registerForm.control}
              rules={numberOfQuestionsValidationRule}
              render={({
                field: { onChange, onBlur, value, name, ref },
                fieldState: { error },
              }) => {
                return (
                  <InputWithLabel
                    label={t('survey.SurveyBasicInfoBox.questionNumber')}
                    name={name}
                    value={value}
                    placeholder={t('survey.SurveyBasicInfoBox.questionNumber')}
                    errorMessage={error?.message}
                    width="125px"
                    textAlign="right"
                    fontWeight={FONT_WEIGHTS.size1}
                    inputFontSize={FONT_SIZES.body1}
                    handleChange={(e) => {
                      // 最初の0を取るためにNumber()して、バリデーションを発火させるために最後にString()する
                      onChange(String(Number(e.target.value.replace(/[^0-9]/g, ''))));
                      handleChangeQuestionNumber(e);
                    }}
                    handleBlur={(e) => {
                      onBlur(e);
                    }}
                    required
                    error={Boolean(error)}
                    errorZIndex={Z_INDEX.MAX}
                    errorWidth="max-content"
                    dataTestId="User-SurveyRegister-QuestionNo"
                    ref={ref}
                    inputProps={{ maxLength: 11 }}
                    handleOnKeyDown={(e) => {
                      handleOnKeyDownQuestionNumber(e);
                    }}
                    position="centerAbsolute"
                  />
                );
              }}
            />
            <HelpLinkWrapper>
              <HelpLink
                warnText={t('appCommon.countQuestionNum')}
                warnLink={ROUTES.COMMON.GGL_QUESTION_NUM}
              />
            </HelpLinkWrapper>
          </FlexAlignTop>
        </ContentBox>
        <ButtonBox>
          {showNgChecklistModal ? (
            <>
              <NgChecklistModal
                open={openNgChecklistModals}
                handleClose={() => {
                  setOpenNgChecklistModals(false);
                }}
                handleRegist={async () => {
                  setOpenNgChecklistModals(false);
                  registerForm.handleSubmit(onSubmit());
                }}
                dataTestId="User-SurveyRegister-NgChecklistModal"
                formType={SURVEY_FORM_TYPE.GOOGLE_FORM}
              />
              <Button
                sx={{ width: 148 }}
                color="primary"
                variant="contained"
                key="button1"
                handleClick={async () => {
                  const validate = await registerForm.trigger();
                  if (validate) setOpenNgChecklistModals(true);
                }}
              >
                {t('appCommon.next')}
              </Button>
            </>
          ) : (
            <Button sx={{ width: 148 }} color="primary" variant="contained" type="submit">
              {t('appCommon.next')}
            </Button>
          )}
        </ButtonBox>
      </SurveyRegisterForm>
      <HelpButtonBox>
        <HelpButton href={ROUTES.COMMON.HELP} />
      </HelpButtonBox>
    </>
  );
}

export default SurveyRegisterGoogleForms;
